// Flashcards - build study decks and drill them with a flip-card study mode.
// Everything is stored locally (localStorage). A bulk "paste import" turns a
// pasted term/definition list, a Quizlet export or an Anki text export into a deck in one step, and study
// mode shuffles, flips, and self-grades so a student can track what they know.
import { el, btn, input, ta, sel, field, wrap, note, announce, saveFlag } from "./ui.js";
import { parseImport, previewImport, uid, newCards } from "./flashcards-import.js";
import { readJSON, writeJSON } from "../shared/store.js";
import { KEY, load, mutate, onChange } from "./fc-store.js";
import { bump, dateKey, dayDiff } from "../shared/activity.js";
import { queue, opts } from "../shared/fc-due.js";
import { loadSched, loadLog, dropCards, SCHED_KEY, LOG_KEY } from "./fc-sched.js";
import { finalsOpen } from "../shared/finals-focus.js";

// The public promise for the practice modes. Same words on /about.
export const PROMISE = "Free, with no daily limits, and we won't put them behind a paywall.";
const NOT_SAVED = "Not saved. Your device is out of space for saves.";
const copyLink = (u) => { try { navigator.clipboard.writeText(String(u)).catch(() => {}); } catch { /* no clipboard */ } };

// Study Helper and Make card hand-offs (tools/fc-store.js).
export { listDecks, addCards } from "./fc-store.js";

// Carry codes live in Settings. They're only offered while the server has them on: the app's
// presence beacon notes that under rv:carry, since this page has no beacon of its own.
const carryOn = () => { try { return localStorage.getItem("rv:carry") === "1"; } catch { return false; } };
const signedIn = () => { try { const j = JSON.parse(localStorage.getItem("revault:account") || "null"); return !!(j && j.token); } catch { return false; } };
function openCarry() {
	const url = "/settings#set=carryCode";
	if (window.parent && window.parent !== window) { try { window.parent.postMessage({ type: "revault-open", url }, location.origin); return; } catch { /* open it here instead */ } }
	location.href = url;
}
const carryLink = (text) => btn(text, openCarry, "rv-fc-carry");

// Paste formats. Auto-detect is the free-form list; the others are explicit choices.
const PRESETS = [
	{ id: "auto", label: "Auto-detect", sample: "One card per line, e.g.\nphotosynthesis - how plants make food\nmitosis: cell division\nOsmosis\twater diffusion",
		help: ["One card per line. Separate the term and definition with a Tab, dash, colon, or equals sign."] },
	{ id: "quizlet", label: "Quizlet export", sample: "Paste the text from Quizlet's Export box, e.g.\nmitosis\tcell division\nosmosis\twater moving through a membrane",
		help: ["In Quizlet, open your set, choose the ... menu, then Export. Copy the text and paste it here.",
			"Pick the same separators you chose in Quizlet. Tab and New line are Quizlet's defaults.",
			"Paste the text itself. A link to the set won't work here."] },
	{ id: "anki", label: "Anki (Notes in Plain Text)", sample: "Paste the text of an Anki export, e.g.\n#separator:tab\n#html:true\nmitosis\tcell division<br>makes two cells",
		help: ["In Anki, choose File, then Export. Pick \"Notes in Plain Text\", export, open the .txt file, then copy everything and paste it here.",
			"Formatting is turned into plain text and images or sounds are left out. The first field becomes the front, the second the back."] },
];
// #review (all decks), #review=<deckId>, #sort=<deckId> (the quick sort), the practice modes
// #learn=, #test=, #match=<deckId>, #play=<deckId>, and #exam=<examId> (today's planned cards for
// an exam). Fragments never reach the server.
const PRACTICE = new Set(["learn", "test", "match"]);
function route() {
	const m = /^#(review|sort|learn|test|match|play|exam)(?:=([A-Za-z0-9_-]{1,64}))?$/.exec(location.hash);
	return m && (m[1] === "review" || m[2]) ? { mode: m[1], deck: m[2] || null } : null;
}

// Play settings on this device: calm mode, the last game, best scores.
const PLAY_KEY = "revault:play";
function playPrefs() {
	const v = readJSON(PLAY_KEY, null) || {};
	const best = v.best && typeof v.best === "object" ? v.best : {};
	const n = (x) => (Number.isFinite(x) && x > 0 ? Math.floor(x) : 0);
	return { v: 1, calm: typeof v.calm === "boolean" ? v.calm : null, last: v.last === "meteor" ? "meteor" : "snake", best: { snake: n(best.snake), meteor: n(best.meteor) } };
}

// Practice settings on this device: last test options, Match timer, best Match times.
const PRACTICE_KEY = "revault:practice";
const TYPES = ["mc", "written", "tf", "match"];
function practicePrefs() {
	const v = readJSON(PRACTICE_KEY, null) || {};
	const t = v.test || {};
	return {
		v: 1,
		test: {
			count: t.count === 20 || t.count === "all" ? t.count : 10,
			types: Array.isArray(t.types) && t.types.length ? t.types.filter((x) => TYPES.includes(x)) : ["mc", "written", "tf"],
			answerWith: t.answerWith === "term" || t.answerWith === "both" ? t.answerWith : "definition",
		},
		calm: v.calm !== false, // no timer unless the student turns it on
		bestMatch: v.bestMatch && typeof v.bestMatch === "object" ? v.bestMatch : {},
	};
}
const plural = (n, one, many) => n + " " + (n === 1 ? one : many);

// Remembered while the page is open, so a re-render keeps the chosen format.
const importOpts = { open: false, preset: "auto", term: "tab", termCustom: "", row: "newline", rowCustom: "" };

export function flashcards(root) {
	// db is only what the screen shows. Changes go through change(), which edits what is stored now.
	let db = load();
	// Screen state, never stored: the deck that is open, and the deck in Study all (a reload opens the list).
	let activeId = db.active, studying = null;
	// Typed but not added yet, kept when the list redraws.
	const draft = { front: "", back: "", bulk: "" };
	// "Not saved" line, kept across re-renders; shown only when the browser refused a write.
	const flag = saveFlag();
	const deckById = (id) => db.decks.find((d) => d.id === id);
	const inStore = (d, id) => d.decks.find((x) => x.id === id);
	let busy = false;
	const change = (fn) => {
		busy = true;
		let skipped = false;
		try {
			const ok = mutate((d) => { if (activeId && inStore(d, activeId)) d.active = activeId; const r = fn(d); if (r === false) skipped = true; return r; });
			if (!skipped) flag.set(ok);
			return ok;
		}
		finally { busy = false; db = load(); }
	};
	// Review settings live in the store's opts (m: when they last changed).
	const setOpts = (o) => change((d) => { d.opts = Object.assign({}, d.opts, o, { m: Date.now() }); });
	let settingsOpen = false;
	// Opening a deck in any mode moves it to the start page's "Jump back in".
	const touch = (id) => change((d) => { const x = inStore(d, id); if (!x) return false; x.studiedAt = Date.now(); });

	// Every change re-renders the tool, so put focus back on a sensible control afterwards.
	const refocus = (sel) => { const t = root.querySelector(sel); if (t) t.focus(); return t; };
	function render() {
		db = load();
		if (!deckById(activeId)) activeId = deckById(db.active) ? db.active : null;
		const r = route();
		if (r) return r.mode === "play" ? openPlay(r) : PRACTICE.has(r.mode) ? openPractice(r) : openReview(r);
		const active = deckById(activeId);
		if (active && studying === active.id) return renderStudy(active);
		studying = null;
		renderManage(active);
	}

	// Another tab, the Study Helper or a review elsewhere changed the cards: redraw the deck list.
	// Practice and review screens read the store themselves, and Study all keeps its own order.
	// While a deck name is being typed or the exam form is open, wait until focus leaves.
	let pending = false;
	function live() {
		if (!root.isConnected) { stop(); return; }
		if (busy || route() || studying) return;
		const a = document.activeElement;
		const editing = a && root.contains(a) && a.classList && (a.classList.contains("rv-fc-title") || a.classList.contains("rv-fc-pace"));
		if (root.querySelector(".rv-fc-exam-form") || editing) { pending = true; return; }
		pending = false;
		redraw();
	}
	// Render again with focus back on the same control (by class and position), and in a text box
	// the caret and scroll where they were.
	function redraw() {
		const spot = focusSpot();
		render();
		if (!spot) return;
		const list = root.querySelectorAll("." + spot.cls);
		if (!list.length) return;
		const t = list[Math.min(spot.i, list.length - 1)];
		t.focus();
		if (spot.caret && t.tagName === spot.caret.tag && typeof t.setSelectionRange === "function") {
			try { t.setSelectionRange(spot.caret.a, spot.caret.b, spot.caret.dir); } catch { /* not a text box */ }
			t.scrollTop = spot.caret.top;
		}
	}
	const stop = onChange(live, [KEY, SCHED_KEY, LOG_KEY]);
	root.addEventListener("focusout", () => setTimeout(() => { if (pending && !root.contains(document.activeElement)) live(); }, 0));
	// Where focus is, as its most specific class and position, so a redraw can put it back.
	function focusSpot() {
		const a = document.activeElement;
		if (!a || a === root || !root.contains(a) || !a.classList) return null;
		let best = null;
		for (const cls of String(a.className || "").split(/\s+/).filter((c) => c.startsWith("rv-"))) {
			const list = root.querySelectorAll("." + cls);
			if (!best || list.length < best.n) best = { cls, n: list.length, i: Array.prototype.indexOf.call(list, a) };
		}
		// selectionStart throws on inputs without a caret (checkbox, number in some browsers).
		if (best && (a.tagName === "TEXTAREA" || a.tagName === "INPUT")) {
			try { if (typeof a.selectionStart === "number") best.caret = { tag: a.tagName, a: a.selectionStart, b: a.selectionEnd, dir: a.selectionDirection || "none", top: a.scrollTop || 0 }; } catch { /* no caret here */ }
		}
		return best;
	}

	// --- review (lazy chunk with the scheduler) ------------------------------
	const go = (hash) => { history.replaceState(null, "", location.pathname + location.search + hash); render(); };
	let reviewSeq = 0;
	function openReview(r) {
		const seq = ++reviewSeq;
		root.textContent = "";
		import(__RVM.fsrsJs).then((m) => {
			if (seq !== reviewSeq) return;
			m.default(root, {
				el, btn, wrap, announce, bump,
				loadDb: load,
				back: (deckId) => { if (deckId) activeId = deckId; go(""); refocus(".rv-fc-bar .rv-fc-review-btn") || refocus(".rv-fc-due-btn") || refocus(".rv-fc-decks-head button"); },
				review: (deckId) => go("#review=" + deckId),
				studyAll: (deckId) => { go(""); const d = deckById(deckId); if (d && d.cards.length) { activeId = d.id; studying = d.id; touch(d.id); render(); } },
			}, r);
		}).catch(() => {
			if (seq !== reviewSeq) return;
			const s = wrap(root);
			s.append(note("Review didn't load. Check your connection and try again."), btn("Back to decks", () => go(""), "rv-tbtn"));
		});
	}
	window.addEventListener("hashchange", () => { if (root.isConnected) render(); });

	// --- practice: Learn, Test, Match (their own lazy chunk) ------------------
	// Cards for the next Learn to stick to ("Review those 5" after Play). Used once.
	let learnOnly = null;
	function openPractice(r) {
		const seq = ++reviewSeq;
		if (r.mode === "learn" && learnOnly && learnOnly.deck === r.deck) r.only = learnOnly.ids;
		learnOnly = null;
		root.textContent = "";
		import(__RVM.practiceJs).then((m) => {
			if (seq !== reviewSeq) return;
			m.default(root, {
				el, btn, wrap, announce, bump,
				loadDb: load,
				prefs: practicePrefs,
				savePrefs: (p) => writeJSON(PRACTICE_KEY, p),
				back: (deckId) => { if (deckId) activeId = deckId; go(""); refocus(".rv-fc-bar .rv-fc-" + r.mode + "-btn") || refocus(".rv-fc-decks-head button"); },
			}, r);
		}).catch(() => {
			if (seq !== reviewSeq) return;
			const s = wrap(root);
			s.append(note("Practice didn't load. Check your connection and try again."), btn("Back to decks", () => go(""), "rv-tbtn"));
		});
	}

	// --- Play your deck (its own lazy chunk) ----------------------------------
	function openPlay(r) {
		const seq = ++reviewSeq;
		root.textContent = "";
		// Cards due for review go first in Play. Read here so the game chunk never touches a schedule.
		const dueFirst = queue(db, loadSched(), loadLog(), Date.now(), r.deck).filter((x) => x.due != null).map((x) => x.id);
		import(__RVM.playJs).then((m) => {
			if (seq !== reviewSeq) return;
			m.default(root, {
				el, btn, wrap, announce, bump,
				loadDb: load,
				dueFirst,
				prefs: playPrefs,
				savePrefs: (p) => writeJSON(PLAY_KEY, p),
				learn: (deckId, ids) => { learnOnly = ids && ids.length ? { deck: deckId, ids } : null; go("#learn=" + deckId); },
				back: (deckId) => { if (deckId) activeId = deckId; go(""); refocus(".rv-fc-bar .rv-fc-play-btn") || refocus(".rv-fc-decks-head button"); },
			}, r);
		}).catch(() => {
			if (seq !== reviewSeq) return;
			const s = wrap(root);
			s.append(note("Play didn't load. Check your connection and try again."), btn("Back to decks", () => go(""), "rv-tbtn"));
		});
	}

	// --- deck list + editor --------------------------------------------------
	function renderManage(active) {
		const s = wrap(root); s.classList.add("rv-fc");
		const aside = el("div", "rv-fc-decks");
		const sched = loadSched(), log = loadLog(), now = Date.now();
		if (finalsOpen(now)) {
			const f = el("p", "rv-fc-finals");
			f.append(el("strong", null, "Finals Focus"), "Add your exam dates and we'll spread your cards over the days before each one. Study all is always open.");
			aside.appendChild(f);
		}
		if (db.decks.some((d) => d.cards.length)) aside.appendChild(dueBox(sched, log, now));
		if (db.decks.length) aside.appendChild(examSlot(sched, log, now));
		const head = el("div", "rv-fc-decks-head");
		const gear = btn("Settings", () => { settingsOpen = !settingsOpen; render(); if (!settingsOpen) { refocus(".rv-fc-settings-btn"); return; } const r = Array.from(root.querySelectorAll(".rv-fc-settings input")).find((i) => i.checked); if (r) r.focus(); }, "rv-tbtn rv-fc-settings-btn");
		gear.setAttribute("aria-label", "Flashcards settings");
		gear.setAttribute("aria-expanded", settingsOpen ? "true" : "false");
		head.append(el("span", "rv-fc-decks-title", "Decks"), gear, btn("+ New", newDeck, "rv-tbtn rv-tbtn-accent"));
		aside.appendChild(head);
		if (settingsOpen) aside.appendChild(settingsSheet());
		if (!db.decks.length) {
			aside.appendChild(note("No decks yet. Create one to start."));
			if (carryOn() && !signedIn()) aside.appendChild(carryLink("Used Re:Vault on another device? Enter a carry code."));
		}

		db.decks.forEach((d) => {
			const item = el("button", "rv-fc-deck" + (d.id === activeId ? " active" : ""));
			item.type = "button";
			if (d.id === activeId) item.setAttribute("aria-current", "true");
			const label = el("span", "rv-fc-deck-name", d.name);
			const count = el("span", "rv-fc-deck-count", String(d.cards.length));
			count.setAttribute("aria-label", d.cards.length + (d.cards.length === 1 ? " card" : " cards"));
			item.append(label, count);
			item.addEventListener("click", () => { activeId = d.id; render(); const t = root.querySelector(".rv-fc-title"); if (t) t.focus(); });
			aside.appendChild(item);
		});
		const promise = el("p", "rv-fc-promise");
		promise.append(el("strong", null, "Learn, Test and Match."), " " + PROMISE);
		aside.appendChild(promise);
		if (db.decks.length && carryOn()) aside.appendChild(carryLink("Take my cards with me"));

		const main = el("div", "rv-fc-main");
		main.appendChild(flag);
		if (!active) {
			main.appendChild(note("Select a deck on the left, or create a new one."));
			s.append(aside, main); return;
		}

		// deck toolbar
		const bar = el("div", "rv-fc-bar");
		const title = input("text", { value: active.name });
		title.classList.add("rv-fc-title"); title.setAttribute("aria-label", "Deck name");
		let nameWarned = false;
		title.addEventListener("input", () => {
			const name = title.value.trim() || "Untitled deck";
			const ok = change((d) => { const x = inStore(d, active.id); if (!x) return false; x.name = name; });
			// Said once while saving keeps failing, not on every key.
			if (!ok && deckById(active.id) && !nameWarned) { nameWarned = true; announce(NOT_SAVED); }
			if (ok) nameWarned = false;
			const li = aside.querySelector(".rv-fc-deck.active .rv-fc-deck-name"); if (li) li.textContent = name;
		});
		const dn = queue(db, sched, log, now, active.id).length;
		const rev = btn(dn ? "Review (" + dn + ")" : "Review", () => { touch(active.id); go("#review=" + active.id); }, "rv-tbtn rv-tbtn-accent rv-fc-review-btn");
		rev.setAttribute("aria-label", dn ? "Review, " + plural(dn, "card", "cards") + " due" : "Review, nothing due");
		const prac = ["learn", "test", "match", "play"].map((mode) => {
			const b = btn(mode[0].toUpperCase() + mode.slice(1), () => { touch(active.id); go("#" + mode + "=" + active.id); }, "rv-tbtn rv-fc-" + mode + "-btn");
			if (!active.cards.length) { b.disabled = true; b.title = "Add cards first"; }
			return b;
		});
		const study = btn("Study all", () => { if (!active.cards.length) return; studying = active.id; touch(active.id); render(); }, "rv-tbtn");
		if (!active.cards.length) { study.disabled = true; study.title = "Add cards first"; rev.disabled = true; rev.title = "Add cards first"; }
		const del = btn("Delete deck", () => {
			if (!confirm("Delete \"" + active.name + "\" and its cards?")) return;
			let gone = [];
			const before = activeId;
			const ok = change((d) => {
				const x = inStore(d, active.id); if (!x) return false;
				gone = x.cards.map((c) => c.id);
				d.decks = d.decks.filter((y) => y.id !== active.id);
				activeId = d.decks[0] ? d.decks[0].id : null;
				d.active = activeId;
			});
			// Progress goes only once the deck is really gone, so a failed save loses nothing.
			if (!ok) { activeId = before; render(); announce(deckById(before) ? NOT_SAVED : "That deck was deleted."); refocus(".rv-fc-bar .rv-tbtn-danger"); return; }
			dropCards(gone); render(); announce("Deck deleted"); refocus(".rv-fc-deck.active") || refocus(".rv-fc-decks-head button");
		}, "rv-tbtn rv-tbtn-danger");
		bar.append(title, rev, ...prac, study, del);
		main.appendChild(bar);
		// A deck that has never been reviewed: one pass to mark what the student already knows.
		if (active.cards.length >= 5 && !active.cards.some((c) => sched.c[c.id])) {
			const offer = el("div", "rv-fc-sort");
			offer.append(btn("I already know most of these", () => go("#sort=" + active.id), "rv-tbtn"), el("span", "rv-fc-sort-hint", "A quick sort so cards you know don't all come back today."));
			main.appendChild(offer);
		}

		// add-card row
		const add = el("form", "rv-fc-add");
		const front = input("text", { placeholder: "Term / question", value: draft.front });
		const back = input("text", { placeholder: "Definition / answer", value: draft.back });
		front.classList.add("rv-fc-add-front"); back.classList.add("rv-fc-add-back");
		front.addEventListener("input", () => { draft.front = front.value; });
		back.addEventListener("input", () => { draft.back = back.value; });
		front.setAttribute("aria-label", "Term or question");
		back.setAttribute("aria-label", "Definition or answer");
		const addBtn = btn("Add", null, "rv-tbtn");
		addBtn.type = "submit"; // btn() makes plain buttons; this one has to submit the form
		add.append(front, back, addBtn);
		add.addEventListener("submit", (e) => {
			e.preventDefault();
			const f = front.value.trim(), b = back.value.trim();
			if (!f || !b) return;
			let n = 0;
			const ok = change((d) => { const x = inStore(d, active.id); if (!x) return false; x.cards.push({ id: uid(), front: f, back: b }); n = x.cards.length; });
			if (!ok) { announce(n ? NOT_SAVED : "That deck was deleted."); render(); return; }
			draft.front = ""; draft.back = "";
			render(); announce("Card added. " + n + (n === 1 ? " card" : " cards") + " in this deck.");
			refocus(".rv-fc-add input");
		});
		main.appendChild(add);

		// bulk import (collapsible)
		const bulkWrap = el("details", "rv-fc-bulk");
		if (importOpts.open) bulkWrap.open = true;
		bulkWrap.addEventListener("toggle", () => { importOpts.open = bulkWrap.open; });
		const sum = el("summary", null, "Paste a list to import");
		const preset = sel(PRESETS.map((p) => [p.id, p.label]), importOpts.preset);
		preset.classList.add("rv-fc-preset");
		const presetField = field("Format", preset);
		const qTerm = sel([["tab", "Tab"], ["comma", "Comma"], ["custom", "Custom"]], importOpts.term);
		const qTermCustom = input("text", { value: importOpts.termCustom, placeholder: "e.g. ::" });
		qTermCustom.setAttribute("aria-label", "Custom text between term and definition");
		const qRow = sel([["newline", "New line"], ["semicolon", "Semicolon"], ["custom", "Custom"]], importOpts.row);
		const qRowCustom = input("text", { value: importOpts.rowCustom, placeholder: "e.g. ##" });
		qRowCustom.setAttribute("aria-label", "Custom text between rows");
		const termBox = el("div", "rv-fc-sep"); termBox.append(field("Between term and definition", qTerm), qTermCustom);
		const rowBox = el("div", "rv-fc-sep"); rowBox.append(field("Between rows", qRow), qRowCustom);
		const qOpts = el("div", "rv-fc-qopts"); qOpts.append(termBox, rowBox);
		const help = el("div", "rv-fc-bulk-help");
		help.id = "rv-fc-bulk-help";
		const bulkTa = ta({ value: draft.bulk });
		bulkTa.classList.add("rv-fc-bulk-ta");
		bulkTa.setAttribute("aria-label", "Cards to import");
		bulkTa.setAttribute("aria-describedby", help.id);
		bulkTa.spellcheck = false;
		const found = el("div", "rv-fc-bulk-found");
		found.setAttribute("aria-live", "polite");
		const opts = () => ({ term: importOpts.term, termCustom: importOpts.termCustom, row: importOpts.row, rowCustom: importOpts.rowCustom });
		// The count is a preview: it waits for a pause in typing and only reads the start of
		// a very large paste. Import below always parses the whole text.
		let previewTimer = 0;
		const preview = () => {
			clearTimeout(previewTimer);
			const text = bulkTa.value;
			if (!text.trim()) { found.textContent = ""; return; }
			if (text.length < 2048 && /^\s*(https?:\/\/|www\.)\S*\s*$/i.test(text)) { found.textContent = "That looks like a link. Paste the exported text instead."; return; }
			const { count: n, partial } = previewImport(text, importOpts.preset, opts());
			if (partial) found.textContent = n ? "At least " + n.toLocaleString() + (n === 1 ? " card" : " cards") + " found. Import reads the whole list." : "No cards found in the first part yet";
			else found.textContent = n ? n + (n === 1 ? " card found" : " cards found") : "No cards found yet";
		};
		const previewSoon = () => { clearTimeout(previewTimer); previewTimer = setTimeout(preview, 150); };
		const sync = () => {
			const p = PRESETS.find((x) => x.id === importOpts.preset) || PRESETS[0];
			help.textContent = "";
			p.help.forEach((line) => help.appendChild(el("p", null, line)));
			bulkTa.placeholder = p.sample;
			qOpts.hidden = p.id !== "quizlet";
			qTermCustom.hidden = importOpts.term !== "custom";
			qRowCustom.hidden = importOpts.row !== "custom";
			preview();
		};
		preset.addEventListener("change", () => { importOpts.preset = preset.value; sync(); });
		// Picking Custom shows the text box right after the select, next in Tab order. Focus
		// stays on the select: arrow keys change a closed select, so moving focus here would
		// pull a keyboard user out of it while they browse the options.
		qTerm.addEventListener("change", () => { importOpts.term = qTerm.value; sync(); });
		qRow.addEventListener("change", () => { importOpts.row = qRow.value; sync(); });
		qTermCustom.addEventListener("input", () => { importOpts.termCustom = qTermCustom.value; previewSoon(); });
		qRowCustom.addEventListener("input", () => { importOpts.rowCustom = qRowCustom.value; previewSoon(); });
		bulkTa.addEventListener("input", () => { draft.bulk = bulkTa.value; previewSoon(); });
		const bulkBtn = btn("Import cards", () => {
			clearTimeout(previewTimer);
			const parsed = parseImport(bulkTa.value, importOpts.preset, opts());
			if (!parsed.length) { bulkBtn.textContent = "No cards found"; setTimeout(() => (bulkBtn.textContent = "Import cards"), 1400); return; }
			// Cards already in this deck (same front and back) aren't added twice.
			let added = 0, skipped = 0;
			const ok = change((d) => {
				const x = inStore(d, active.id); if (!x) return false;
				const r = newCards(x.cards, parsed);
				added = r.cards.length; skipped = r.skipped;
				if (!added) return false;
				x.cards.push(...r.cards);
			});
			const skip = skipped ? " Skipped " + plural(skipped, "card", "cards") + " already in this deck." : "";
			if (!added) { found.textContent = (deckById(active.id) ? "No new cards." : "That deck was deleted.") + skip; announce(found.textContent); return; }
			if (!ok) { found.textContent = NOT_SAVED; announce(found.textContent); return; }
			importOpts.open = false; draft.bulk = ""; render();
			announce(plural(added, "card", "cards") + " imported." + skip);
			refocus(".rv-fc-add input");
		}, "rv-tbtn rv-tbtn-accent");
		const actions = el("div", "rv-fc-bulk-actions"); actions.append(bulkBtn, found);
		bulkWrap.append(sum, presetField, qOpts, help, bulkTa, actions);
		sync();
		main.appendChild(bulkWrap);

		// card list
		const listHead = el("div", "rv-fc-list-head");
		listHead.append(el("span", null, active.cards.length + (active.cards.length === 1 ? " card" : " cards")));
		if (active.cards.length) listHead.append(btn("Clear all", () => {
			if (!confirm("Remove all cards from this deck?")) return;
			// Only the cards shown when Clear all was pressed: one added elsewhere since stays.
			const shown = new Set(active.cards.map((c) => c.id));
			const ok = change((d) => { const x = inStore(d, active.id); if (!x) return false; x.cards = x.cards.filter((c) => !shown.has(c.id)); });
			if (!ok) { render(); announce(deckById(active.id) ? NOT_SAVED : "That deck was deleted."); refocus(".rv-fc-add input"); return; }
			dropCards([...shown]); render(); refocus(".rv-fc-add input");
		}, "rv-tbtn rv-tbtn-danger"));
		main.appendChild(listHead);

		const list = el("div", "rv-fc-list");
		active.cards.forEach((c) => {
			const rowEl = el("div", "rv-fc-card-row");
			rowEl.append(el("span", "rv-fc-card-front", c.front), el("span", "rv-fc-card-sep", "→"), el("span", "rv-fc-card-back", c.back));
			// Made from a highlight: where it came from, as plain text (the address is never a link).
			const src = c.src && typeof c.src === "object" ? c.src : null;
			if (src && (typeof src.t === "string" || typeof src.u === "string")) {
				const from = el("div", "rv-fc-card-src", "From: " + (String(src.t || "") || String(src.u || "")) + (typeof src.d === "string" && src.d ? " (" + src.d + ")" : ""));
				if (typeof src.u === "string" && src.u) from.append(" ", btn("Copy link", () => { copyLink(src.u); announce("Link copied"); }, "rv-fc-card-copy"));
				rowEl.append(from);
			}
			const rm = btn("✕", () => {
				const at = active.cards.findIndex((x) => x.id === c.id);
				const ok = change((d) => { const x = inStore(d, active.id); if (!x) return false; x.cards = x.cards.filter((y) => y.id !== c.id); });
				if (ok) dropCards([c.id]);
				render();
				announce(ok ? "Card deleted" : deckById(active.id) ? NOT_SAVED : "That deck was deleted.");
				const left = root.querySelectorAll(".rv-fc-card-del");
				if (left.length) left[Math.min(at, left.length - 1)].focus(); else refocus(".rv-fc-add input");
			}, "rv-fc-card-del");
			rm.setAttribute("aria-label", "Delete card: " + c.front);
			rowEl.append(rm);
			list.appendChild(rowEl);
		});
		main.appendChild(list);

		s.append(aside, main);
	}

	// "18 cards due" over the deck list. Same queue as the start page chip and the review screen.
	function dueBox(sched, log, now) {
		const q = queue(db, sched, log, now);
		const box = el("div", "rv-fc-due");
		const n = q.length;
		if (!n) box.append(el("p", "rv-fc-due-text", "All caught up. New cards come back tomorrow."));
		else {
			box.append(el("p", "rv-fc-due-text", n > 200 ? "200+ cards due. Any amount helps." : plural(n, "card", "cards") + " due")); // not "today": the study day runs to 4 am
			box.append(btn("Review now", () => go("#review"), "rv-tbtn rv-tbtn-accent rv-fc-due-btn"));
		}
		const decks = new Set(q.map((x) => x.deck)).size;
		const on = opts(db).mix;
		if (on && decks > 1) box.append(el("p", "rv-fc-due-mix", "Mixing " + decks + " decks. Mixing topics can help on tests."));
		// Shown whenever there is more than one deck with cards, so it can always be switched back.
		if (db.decks.filter((d) => d.cards.length).length > 1) {
			const t = btn(on ? "Mix decks: on" : "Mix decks: off", () => {
				const ok = setOpts({ mix: !on });
				render();
				announce(!ok ? NOT_SAVED : on ? "Mix decks off. Review goes deck by deck." : "Mix decks on.");
				refocus(".rv-fc-mix-btn");
			}, "rv-tbtn rv-fc-mix-btn");
			t.setAttribute("aria-label", "Mix decks");
			t.setAttribute("aria-pressed", on ? "true" : "false");
			box.append(t);
		}
		return box;
	}

	// The settings sheet: how much to remember, the New-card pace, Mix decks. Each change saves right away.
	function settingsSheet() {
		const o = opts(db);
		const sheet = el("div", "rv-fc-settings");
		let hints = 0;
		const set = (legend, hint, ...kids) => {
			const f = el("fieldset", "rv-pr-set rv-fc-set");
			const lg = el("legend", null, legend);
			f.append(lg, ...kids);
			if (hint) { const h = el("p", "rv-fc-set-hint", hint); h.id = "rv-fc-set-hint-" + ++hints; f.setAttribute("aria-describedby", h.id); f.appendChild(h); }
			return f;
		};
		// Saved right away, then the deck list redraws (the due count and Mix button follow these).
		// The redraw waits a tick: the number box saves on blur, and focus has to land on whatever
		// the student moved to before it is put back there.
		const save = (o, said) => {
			const ok = setOpts(o);
			setTimeout(() => { if (root.isConnected && !route()) redraw(); announce(ok ? said : NOT_SAVED); }, 0);
		};
		const radios = [[0.85, "85%"], [0.9, "90% (recommended)"], [0.95, "95%"]].map(([v, label]) => {
			const w = el("label", "rv-pr-opt");
			const i = el("input", "rv-fc-ret"); i.type = "radio"; i.name = "rv-fc-ret"; i.value = String(v); i.checked = o.ret === v;
			i.addEventListener("change", () => { if (!i.checked) return; save({ ret: v }, "Remember " + Math.round(v * 100) + "% saved."); });
			w.append(i, el("span", null, label));
			return w;
		});
		const pace = el("input", "rv-tinput rv-fc-pace");
		pace.type = "number"; pace.min = "0"; pace.max = "999"; pace.step = "1"; pace.value = String(o.newPerDay);
		pace.setAttribute("aria-label", "New cards a day");
		pace.addEventListener("change", () => {
			const n = Math.round(Number(pace.value));
			if (!Number.isFinite(n) || n < 0 || n > 999 || pace.value.trim() === "") { pace.value = String(opts(db).newPerDay); announce("Pick a number from 0 to 999."); return; }
			pace.value = String(n);
			save({ newPerDay: n }, "New cards a day: " + n + ".");
		});
		const mixW = el("label", "rv-pr-opt");
		const mix = el("input"); mix.type = "checkbox"; mix.checked = o.mix; mix.className = "rv-fc-mix-check";
		mix.addEventListener("change", () => { save({ mix: mix.checked }, mix.checked ? "Mix decks on." : "Mix decks off. Review goes deck by deck."); });
		mixW.append(mix, el("span", null, "Mix decks when reviewing"));
		const done = btn("Done", () => { settingsOpen = false; render(); refocus(".rv-fc-settings-btn"); }, "rv-tbtn");
		sheet.append(
			set("How much do you want to remember?", "Higher means more reviews each day.", ...radios),
			set("New cards a day", "This sets the pace for Due today. It never stops you. Study all and every practice mode stay open.", pace),
			set("Reviewing", null, mixW),
			done,
		);
		return sheet;
	}

	// --- cram planner: the exams box lives in the review chunk (it needs the scheduler anyway) ---
	// With no exams to show, the deck list draws the empty box itself and the chunk loads only when
	// "Add an exam" is pressed.
	let examsWanted = false;
	function examSlot(sched, log, now) {
		const slot = el("div", "rv-fc-exams-slot");
		if (!examsWanted && !hasExams(now)) {
			const box = el("div", "rv-fc-exams");
			const head = el("div", "rv-fc-exams-head");
			const add = btn("Add an exam", () => {
				add.disabled = true;
				import(__RVM.fsrsJs).then((m) => {
					examsWanted = true;
					m.openExamForm();
					if (root.isConnected && !route()) render();
				}).catch(() => { add.disabled = false; add.focus(); announce("Exams didn't load. Check your connection and try again."); });
			}, "rv-tbtn rv-fc-exam-add");
			head.append(el("span", null, "Exams"), add);
			box.append(head, el("p", "rv-fc-exam-line", "Add an exam and we'll spread your cards over the days before it."));
			slot.appendChild(box);
			return slot;
		}
		examsWanted = true; // from here the chunk draws the box, even once the last exam is removed
		import(__RVM.fsrsJs).then((m) => {
			if (!slot.isConnected) return;
			m.exams(slot, { el, btn, field, input, announce, db: Object.assign({}, db, { active: activeId }), sched, log, now, go, render, refocus });
		}).catch(() => { /* the deck list works without it; Review says if the chunk can't load */ });
		return slot;
	}

	// Any exam the box would list (a passed one stays 3 days). Read raw: the store comes with the chunk.
	function hasExams(now) {
		const p = readJSON("revault:plan", null);
		if (!p || !Array.isArray(p.exams)) return false;
		const del = p.del && typeof p.del === "object" ? p.del : {};
		const today = dateKey(new Date(now));
		return p.exams.some((x) => x && typeof x.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x.date) && !(del[x.id] >= Math.max(Number.isFinite(x.ts) ? x.ts : 0, Number.isFinite(x.m) ? x.m : 0)) && dayDiff(today, x.date) >= -3);
	}

	function newDeck() {
		const d = { id: uid(), name: "Untitled deck", cards: [] };
		const before = activeId;
		activeId = d.id;
		if (!change((x) => { x.decks.unshift(d); x.active = d.id; })) { activeId = before; render(); announce(NOT_SAVED); refocus(".rv-fc-decks-head .rv-tbtn-accent"); return; }
		render();
		const t = refocus(".rv-fc-title"); // name it right away
		if (t) t.select();
	}

	// --- study mode ----------------------------------------------------------
	function renderStudy(deck) {
		const s = wrap(root); s.classList.add("rv-fc-study");
		// A fresh shuffled order + per-session "known" set each time study starts.
		let order = deck.cards.map((_, i) => i);
		for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
		let pos = 0, flipped = false;
		const known = new Set();
		const counted = new Set(); // each card counts once per study session in the ledger
		// Reviewed (either answer) also counts once per card: 5 of them fill a school-day streak day.
		const reviewed = new Set();
		const review = (c) => { if (c && !reviewed.has(c.id)) { reviewed.add(c.id); bump("fr"); } };

		const bar = el("div", "rv-fc-study-bar");
		const exit = btn("← Decks", () => { studying = null; render(); refocus(".rv-fc-bar .rv-tbtn-accent"); }, "rv-tbtn");
		const name = el("span", "rv-fc-study-name", deck.name);
		const shuffle = btn("Shuffle", () => { for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; } pos = 0; flipped = false; paint(); }, "rv-tbtn");
		const prog = el("span", "rv-fc-study-prog");
		prog.setAttribute("role", "status");
		bar.append(exit, name, prog, shuffle);

		const card = el("div", "rv-fc-flip"); card.setAttribute("role", "button"); card.tabIndex = 0;
		const inner = el("div", "rv-fc-flip-inner");
		const faceF = el("div", "rv-fc-face rv-fc-face-front");
		const faceB = el("div", "rv-fc-face rv-fc-face-back");
		inner.append(faceF, faceB); card.appendChild(inner);
		const hint = el("div", "rv-fc-flip-hint", "Click the card or press Space to flip");
		hint.id = "rv-fc-hint";
		card.setAttribute("aria-describedby", hint.id);

		const nav = el("div", "rv-fc-study-nav");
		const prev = btn("‹ Prev", () => step(-1), "rv-tbtn");
		const gotIt = btn("Got it", () => { const c = deck.cards[order[pos]]; review(c); if (c && !counted.has(c.id)) { counted.add(c.id); bump("fc"); } known.add(order[pos]); step(1); }, "rv-tbtn rv-tbtn-accent");
		const again = btn("Again", () => { review(deck.cards[order[pos]]); known.delete(order[pos]); step(1); }, "rv-tbtn");
		const next = btn("Next ›", () => step(1), "rv-tbtn");
		nav.append(prev, again, gotIt, next);

		const done = el("div", "rv-fc-done"); done.hidden = true;

		function paint() {
			const total = order.length;
			if (pos >= total) {
				card.hidden = true; hint.hidden = true; nav.hidden = true;
				done.hidden = false; done.textContent = "";
				const kc = known.size;
				done.append(el("div", "rv-fc-done-title", "Session complete"),
					el("div", "rv-fc-done-sub", "You marked " + kc + " of " + total + " as known."),
					btn("Study again", () => { pos = 0; flipped = false; known.clear(); card.hidden = false; hint.hidden = false; nav.hidden = false; done.hidden = true; paint(); card.focus(); }, "rv-tbtn rv-tbtn-accent"));
				prog.textContent = total + " / " + total;
				// The buttons that were focused are now hidden; land on the summary's button.
				announce("Session complete. You marked " + kc + " of " + total + " as known.");
				const again = done.querySelector("button");
				if (again && (nav.contains(document.activeElement) || card === document.activeElement || document.activeElement === document.body)) again.focus();
				return;
			}
			const c = deck.cards[order[pos]];
			faceF.textContent = c.front; faceB.textContent = c.back;
			card.classList.toggle("flipped", flipped);
			showFace();
			prog.textContent = (pos + 1) + " / " + total + (known.size ? "  ·  " + known.size + " known" : "");
			prev.disabled = pos === 0;
		}
		function step(dir) { pos += dir; flipped = false; if (pos < 0) pos = 0; paint(); }
		// Only the face that is showing is in the accessibility tree, so the answer isn't read early.
		function showFace() {
			faceF.setAttribute("aria-hidden", flipped ? "true" : "false");
			faceB.setAttribute("aria-hidden", flipped ? "false" : "true");
			card.setAttribute("aria-label", (flipped ? "Answer: " + faceB.textContent : "Question: " + faceF.textContent));
		}
		function flip() {
			flipped = !flipped; card.classList.toggle("flipped", flipped); showFace();
			if (flipped) announce("Answer: " + faceB.textContent);
		}

		card.addEventListener("click", flip);
		card.addEventListener("keydown", (e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); e.stopPropagation(); flip(); } });
		// Keyboard drive for study mode (arrows + space), scoped to this session.
		const onKey = (e) => {
			if (!document.body.contains(card)) { window.removeEventListener("keydown", onKey); return; }
			if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
			// Space on a focused button presses that button; it only flips from the card or the page.
			if (e.key === " " && e.target && e.target !== card && (e.target.tagName === "BUTTON" || e.target.getAttribute("role") === "button")) return;
			if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
			else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
			else if (e.key === " ") { e.preventDefault(); flip(); }
		};
		window.addEventListener("keydown", onKey);

		s.append(flag, bar, card, hint, nav, done);
		paint();
		card.focus();
	}

	render();
}
